import React, { useState } from 'react';
import ModulePalette from './ModulePalette';
import { formatAreaLabel } from './sitePlannerGeometry';
import {
    Layers,
    Settings,
    MapPin,
    FileCode,
    CheckCircle,
    RotateCcw,
    Scissors,
    MousePointer2,
    Crosshair,
    Grid3x3,
    Download,
    Route,
} from 'lucide-react';

export const DXF_UNIT_OPTIONS = [
    { value: 'm', label: 'Meters (m)' },
    { value: 'mm', label: 'Millimeters (mm)' },
    { value: 'in', label: 'Inches (in)' },
    { value: 'ft', label: 'Feet (ft)' },
    { value: 'yd', label: 'Yards (yd)' },
];

const WING_OPTIONS = [
    { id: 'E', label: 'East' },
    { id: 'W', label: 'West' },
    { id: 'N', label: 'North' },
    { id: 'S', label: 'South' },
];

const CAMPUS_LAYOUT_LABELS = {
    campus_mirrored: 'Mirrored rows',
    campus_linear: 'Single row',
    campus_wings_mirrored: 'Wings, mirrored',
    campus_wings_linear: 'Wings, single row',
};

const MANUAL_TOOLS = [
    { id: 'split', label: 'Division Line', icon: Scissors },
    { id: 'corridor', label: 'Corridor', icon: Route },
    { id: 'select', label: 'Selection', icon: MousePointer2 },
    { id: 'hatchOrigin', label: 'Hatch Origin', icon: Crosshair },
];

function useNumericDrafts() {
    const [drafts, setDrafts] = useState({});

    const getDisplay = (key, stored) =>
        drafts[key] !== undefined ? drafts[key] : (stored ?? '');

    const setDraft = (key, value) => {
        setDrafts((prev) => ({ ...prev, [key]: value }));
    };

    const clearDraft = (key) => {
        setDrafts((prev) => {
            if (prev[key] === undefined) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    return { getDisplay, setDraft, clearDraft };
}

export default function ParameterPanel({
    mode,
    setMode,
    setback,
    setSetback,
    m1Params,
    setM1Params,
    m2Params,
    setM2Params,
    m3Params,
    setM3Params,
    m4Params,
    setM4Params,
    optimizationMode,
    setOptimizationMode,
    groupConfig,
    setGroupConfig,
    campusConfig,
    setCampusConfig,
    layoutObjective,
    setLayoutObjective,
    maxGroups,
    setMaxGroups,
    clusterRadius,
    setClusterRadius,
    onAcceptAllGroups,
    infraPaletteTiles,
    dxfLayers,
    visibleLayers,
    toggleLayer,
    onRunOptimize,
    onClearAll,
    results,
    onUploadDxf,
    dxfUnits,
    setDxfUnits,
    dxfScale,
    setDxfScale,
    panelTab,
    setPanelTab,
    manualMode,
    setManualMode,
    toolMode,
    setToolMode,
    corridorWidth,
    setCorridorWidth,
    subAreas,
    selectedAreaIds,
    setSelectedAreaIds,
    areaHatchConfigs,
    updateAreaHatchConfig,
    hatchAngleStep,
    setHatchAngleStep,
    areaLayouts,
    manualModuleCount,
    m2Dirty,
    onApplyHatch,
    onExportDxf,
    exportDisabled,
    canExportDxf,
    historySlot,
    onPaletteDragStart,
    paletteDragType,
}) {
    const { getDisplay, setDraft, clearDraft } = useNumericDrafts();

    const handleNumericDraftChange = (key, raw, onValid) => {
        setDraft(key, raw);
        if (raw === '' || raw === '-' || raw === '.') return;
        const val = parseFloat(raw);
        if (!Number.isNaN(val)) onValid(val);
    };

    const handleNumericDraftBlur = (key, stored, onValid) => {
        const raw = getDisplay(key, stored);
        if (raw === '' || raw === '-') {
            clearDraft(key);
            return;
        }
        const val = parseFloat(raw);
        if (!Number.isNaN(val)) {
            onValid(val);
        }
        clearDraft(key);
    };

    return (
        <div className="sp-panel">
            <div className="sp-panel-header">
                <div>
                    <h1 className="sp-title">Site Planner Studio</h1>
                    <p className="sp-subtitle">Spatial optimization & layout tool</p>
                </div>
                {historySlot}
            </div>

            <div className="sp-input-group">
                <label className="sp-section-title">Planning Mode</label>
                <div className="sp-toggle-group">
                    <button
                        type="button"
                        onClick={() => setMode('map')}
                        className={`sp-toggle-btn ${mode === 'map' ? 'active' : ''}`}
                    >
                        <MapPin size={16} />
                        Map
                    </button>
                    <button
                        type="button"
                        onClick={() => setMode('dxf')}
                        className={`sp-toggle-btn ${mode === 'dxf' ? 'active' : ''}`}
                    >
                        <FileCode size={16} />
                        DXF CAD
                    </button>
                </div>
            </div>

            <div className="sp-panel-tabs">
                <button
                    type="button"
                    className={`sp-panel-tab ${panelTab === 'global' ? 'active' : ''}`}
                    onClick={() => setPanelTab('global')}
                >
                    Global Controls
                </button>
                <button
                    type="button"
                    className={`sp-panel-tab ${panelTab === 'manual' ? 'active' : ''}`}
                    onClick={() => setPanelTab('manual')}
                >
                    Manual Layout
                </button>
            </div>

            {panelTab === 'global' && (
                <>
                    <div className="sp-flex-col sp-gap-4">
                        <div
                            className="sp-flex-row sp-gap-2 sp-section-title"
                            style={{ borderBottom: '1px solid #1e293b', paddingBottom: '0.5rem', color: '#2dd4bf' }}
                        >
                            <Settings size={16} />
                            <span>Global Controls</span>
                        </div>

                        <div className="sp-input-group">
                            <div className="sp-slider-label">
                                <span>Setback (Clearance to boundary)</span>
                                <span style={{ color: '#2dd4bf', fontWeight: '500' }}>{setback}m</span>
                            </div>
                            <input
                                type="range"
                                min="0"
                                max="20"
                                step="0.5"
                                value={setback}
                                onChange={(e) => setSetback(parseFloat(e.target.value))}
                                className="sp-slider"
                            />
                        </div>
                    </div>

                    <div className="sp-card">
                        <div className="sp-section-title">Module 1 (Datahall)</div>
                        <div className="sp-grid-2">
                            <div className="sp-flex-col">
                                <span className="sp-input-label">Length (m)</span>
                                <input
                                    type="number"
                                    value={m1Params.length}
                                    onChange={(e) =>
                                        setM1Params({ ...m1Params, length: parseFloat(e.target.value) || 0 })
                                    }
                                    className="sp-number-input"
                                />
                            </div>
                            <div className="sp-flex-col">
                                <span className="sp-input-label">Width (m)</span>
                                <input
                                    type="number"
                                    value={m1Params.width}
                                    onChange={(e) =>
                                        setM1Params({ ...m1Params, width: parseFloat(e.target.value) || 0 })
                                    }
                                    className="sp-number-input"
                                />
                            </div>
                        </div>
                        <div className="sp-grid-2">
                            <div className="sp-flex-col">
                                <span className="sp-input-label">Height (m)</span>
                                <input
                                    type="number"
                                    value={m1Params.height ?? 2.5}
                                    onChange={(e) =>
                                        setM1Params({ ...m1Params, height: parseFloat(e.target.value) || 0 })
                                    }
                                    className="sp-number-input"
                                />
                            </div>
                        </div>
                        <div className="sp-grid-2">
                            <div className="sp-flex-col">
                                <span className="sp-input-label">Row Gap (m)</span>
                                <input
                                    type="number"
                                    value={m1Params.gap_width}
                                    onChange={(e) =>
                                        setM1Params({ ...m1Params, gap_width: parseFloat(e.target.value) || 0 })
                                    }
                                    className="sp-number-input"
                                />
                            </div>
                            <div className="sp-flex-col">
                                <span className="sp-input-label">End Gap (m)</span>
                                <input
                                    type="number"
                                    value={m1Params.gap_length}
                                    onChange={(e) =>
                                        setM1Params({ ...m1Params, gap_length: parseFloat(e.target.value) || 0 })
                                    }
                                    className="sp-number-input"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="sp-card">
                        <div className="sp-section-title">Module 2 (MV-LV train)</div>
                        <div className="sp-grid-2">
                            <div className="sp-flex-col">
                                <span className="sp-input-label">Length (m)</span>
                                <input
                                    type="number"
                                    value={m2Params.length}
                                    onChange={(e) =>
                                        setM2Params({ ...m2Params, length: parseFloat(e.target.value) || 0 })
                                    }
                                    className="sp-number-input"
                                />
                            </div>
                            <div className="sp-flex-col">
                                <span className="sp-input-label">Width (m)</span>
                                <input
                                    type="number"
                                    value={m2Params.width}
                                    onChange={(e) =>
                                        setM2Params({ ...m2Params, width: parseFloat(e.target.value) || 0 })
                                    }
                                    className="sp-number-input"
                                />
                            </div>
                        </div>
                        <div className="sp-grid-2">
                            <div className="sp-flex-col">
                                <span className="sp-input-label">Height (m)</span>
                                <input
                                    type="number"
                                    value={m2Params.height ?? 2.5}
                                    onChange={(e) =>
                                        setM2Params({ ...m2Params, height: parseFloat(e.target.value) || 0 })
                                    }
                                    className="sp-number-input"
                                />
                            </div>
                        </div>
                        <div style={{ fontSize: '10px', color: '#64748b' }}>
                            Default group is one datahall and four trains: two chained pairs under the hall’s long side.
                        </div>
                    </div>

                    <div className="sp-card">
                        <div className="sp-section-title">Module 3 (GIS)</div>
                        <div className="sp-grid-2">
                            <div className="sp-flex-col">
                                <span className="sp-input-label">Length (m)</span>
                                <input
                                    type="number"
                                    value={m3Params.length}
                                    onChange={(e) =>
                                        setM3Params({ ...m3Params, length: parseFloat(e.target.value) || 0 })
                                    }
                                    className="sp-number-input"
                                />
                            </div>
                            <div className="sp-flex-col">
                                <span className="sp-input-label">Width (m)</span>
                                <input
                                    type="number"
                                    value={m3Params.width}
                                    onChange={(e) =>
                                        setM3Params({ ...m3Params, width: parseFloat(e.target.value) || 0 })
                                    }
                                    className="sp-number-input"
                                />
                            </div>
                        </div>
                        <div className="sp-grid-2">
                            <div className="sp-flex-col">
                                <span className="sp-input-label">Height (m)</span>
                                <input
                                    type="number"
                                    value={m3Params.height ?? 3.0}
                                    onChange={(e) =>
                                        setM3Params({ ...m3Params, height: parseFloat(e.target.value) || 0 })
                                    }
                                    className="sp-number-input"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="sp-card">
                        <div className="sp-section-title">Module 4 (TX)</div>
                        <div className="sp-grid-2">
                            <div className="sp-flex-col">
                                <span className="sp-input-label">Length (m)</span>
                                <input
                                    type="number"
                                    value={m4Params.length}
                                    onChange={(e) =>
                                        setM4Params({ ...m4Params, length: parseFloat(e.target.value) || 0 })
                                    }
                                    className="sp-number-input"
                                />
                            </div>
                            <div className="sp-flex-col">
                                <span className="sp-input-label">Width (m)</span>
                                <input
                                    type="number"
                                    value={m4Params.width}
                                    onChange={(e) =>
                                        setM4Params({ ...m4Params, width: parseFloat(e.target.value) || 0 })
                                    }
                                    className="sp-number-input"
                                />
                            </div>
                        </div>
                        <div className="sp-grid-2">
                            <div className="sp-flex-col">
                                <span className="sp-input-label">Height (m)</span>
                                <input
                                    type="number"
                                    value={m4Params.height ?? 2.5}
                                    onChange={(e) =>
                                        setM4Params({ ...m4Params, height: parseFloat(e.target.value) || 0 })
                                    }
                                    className="sp-number-input"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="sp-card">
                        <div className="sp-section-title">Optimization Mode</div>
                        <div className="sp-toggle-group">
                            {[
                                { id: 'groups', label: 'Groups' },
                                { id: 'cluster', label: 'Cluster' },
                                { id: 'hatch', label: 'Max Fill' },
                                { id: 'campus', label: 'Campus' },
                            ].map(({ id, label }) => (
                                <button
                                    key={id}
                                    type="button"
                                    onClick={() => setOptimizationMode(id)}
                                    className={`sp-toggle-btn ${optimizationMode === id ? 'active' : ''}`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                        {optimizationMode === 'groups' && (
                            <div className="sp-flex-col sp-gap-2" style={{ marginTop: '0.75rem' }}>
                                <div className="sp-toggle-group">
                                    {[
                                        { id: 'max_quantity', label: 'Max groups' },
                                        { id: 'min_space', label: 'Min land' },
                                    ].map(({ id, label }) => (
                                        <button
                                            key={id}
                                            type="button"
                                            onClick={() => setLayoutObjective(id)}
                                            className={`sp-toggle-btn ${layoutObjective === id ? 'active' : ''}`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                                <div style={{ fontSize: '10px', color: '#64748b' }}>
                                    Max groups fills the site. Min land packs that count into the smallest rectangle — set Max groups to fix how many.
                                </div>
                                <div className="sp-grid-2">
                                    <div className="sp-flex-col">
                                        <span className="sp-input-label">M1 per group</span>
                                        <input
                                            type="number"
                                            min="1"
                                            value={groupConfig.m1_count}
                                            onChange={(e) =>
                                                setGroupConfig({
                                                    ...groupConfig,
                                                    m1_count: parseInt(e.target.value, 10) || 1,
                                                })
                                            }
                                            className="sp-number-input"
                                        />
                                    </div>
                                    <div className="sp-flex-col">
                                        <span className="sp-input-label">M2 per group</span>
                                        <input
                                            type="number"
                                            min="0"
                                            value={groupConfig.m2_count}
                                            onChange={(e) =>
                                                setGroupConfig({
                                                    ...groupConfig,
                                                    m2_count: parseInt(e.target.value, 10) || 0,
                                                })
                                            }
                                            className="sp-number-input"
                                        />
                                    </div>
                                </div>
                                <div className="sp-grid-2">
                                    <div className="sp-flex-col">
                                        <span className="sp-input-label">Group gap (m)</span>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.5"
                                            value={groupConfig.gap}
                                            onChange={(e) =>
                                                setGroupConfig({
                                                    ...groupConfig,
                                                    gap: parseFloat(e.target.value) || 0,
                                                })
                                            }
                                            className="sp-number-input"
                                        />
                                    </div>
                                    <div className="sp-flex-col">
                                        <span className="sp-input-label">Max groups (power)</span>
                                        <input
                                            type="number"
                                            min="0"
                                            placeholder="No limit"
                                            value={maxGroups ?? ''}
                                            onChange={(e) => {
                                                const raw = e.target.value;
                                                setMaxGroups(raw === '' ? null : parseInt(raw, 10) || 0);
                                            }}
                                            className="sp-number-input"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                        {optimizationMode === 'cluster' && (
                            <div className="sp-flex-col" style={{ marginTop: '0.75rem' }}>
                                <span className="sp-input-label">Interconnect radius (m)</span>
                                <input
                                    type="number"
                                    min="1"
                                    value={clusterRadius}
                                    onChange={(e) => setClusterRadius(parseFloat(e.target.value) || 50)}
                                    className="sp-number-input"
                                />
                            </div>
                        )}
                        {optimizationMode === 'campus' && campusConfig && (
                            <div className="sp-flex-col sp-gap-2" style={{ marginTop: '0.75rem' }}>
                                <div style={{ fontSize: '10px', color: '#64748b' }}>
                                    Halls stay fixed relative to each other. An empty campus maximum fills the site. eHouses stay on the outer face of their hall and lie flat when that strip is shallow.
                                </div>
                                <div style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1' }}>Campuses</div>
                                <div className="sp-grid-2">
                                    <div className="sp-flex-col">
                                        <span className="sp-input-label">Max campuses</span>
                                        <input
                                            type="number"
                                            min="1"
                                            placeholder="All that fit"
                                            value={campusConfig.campus_count || ''}
                                            onChange={(e) => {
                                                const raw = e.target.value;
                                                setCampusConfig({
                                                    ...campusConfig,
                                                    campus_count: raw === '' ? null : Math.max(1, parseInt(raw, 10) || 1),
                                                });
                                            }}
                                            className="sp-number-input"
                                        />
                                    </div>
                                    <div className="sp-flex-col">
                                        <span className="sp-input-label">Gap between (m)</span>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.1"
                                            value={campusConfig.campus_gap ?? 6}
                                            onChange={(e) =>
                                                setCampusConfig({
                                                    ...campusConfig,
                                                    campus_gap: parseFloat(e.target.value) || 0,
                                                })
                                            }
                                            className="sp-number-input"
                                        />
                                    </div>
                                </div>
                                <div style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1' }}>Datahalls</div>
                                <div className="sp-flex-col">
                                    <span className="sp-input-label">Count</span>
                                    <input
                                        type="number"
                                        min="0"
                                        value={campusConfig.datahall_count}
                                        onChange={(e) =>
                                            setCampusConfig({
                                                ...campusConfig,
                                                datahall_count: parseInt(e.target.value, 10) || 0,
                                            })
                                        }
                                        className="sp-number-input"
                                    />
                                </div>
                                <div className="sp-grid-2">
                                    <div className="sp-flex-col">
                                        <span className="sp-input-label">Length (m)</span>
                                        <input
                                            type="number"
                                            min="0.1"
                                            step="0.1"
                                            value={campusConfig.dh_length}
                                            onChange={(e) =>
                                                setCampusConfig({
                                                    ...campusConfig,
                                                    dh_length: parseFloat(e.target.value) || 0,
                                                })
                                            }
                                            className="sp-number-input"
                                        />
                                    </div>
                                    <div className="sp-flex-col">
                                        <span className="sp-input-label">Width (m)</span>
                                        <input
                                            type="number"
                                            min="0.1"
                                            step="0.1"
                                            value={campusConfig.dh_width}
                                            onChange={(e) =>
                                                setCampusConfig({
                                                    ...campusConfig,
                                                    dh_width: parseFloat(e.target.value) || 0,
                                                })
                                            }
                                            className="sp-number-input"
                                        />
                                    </div>
                                </div>
                                <div style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1' }}>Corridors</div>
                                <div className="sp-grid-2">
                                    <div className="sp-flex-col">
                                        <span className="sp-input-label">Side corridor c (m)</span>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.1"
                                            value={campusConfig.corridor_width_c}
                                            onChange={(e) =>
                                                setCampusConfig({
                                                    ...campusConfig,
                                                    corridor_width_c: parseFloat(e.target.value) || 0,
                                                })
                                            }
                                            className="sp-number-input"
                                        />
                                    </div>
                                    <div className="sp-flex-col">
                                        <span className="sp-input-label">CH width (m)</span>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.1"
                                            value={campusConfig.corridor_width_ch}
                                            onChange={(e) =>
                                                setCampusConfig({
                                                    ...campusConfig,
                                                    corridor_width_ch: parseFloat(e.target.value) || 0,
                                                })
                                            }
                                            className="sp-number-input"
                                        />
                                    </div>
                                </div>
                                <div style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1' }}>eHouses</div>
                                <div className="sp-grid-2">
                                    <div className="sp-flex-col">
                                        <span className="sp-input-label">Rows</span>
                                        <input
                                            type="number"
                                            min="0"
                                            value={campusConfig.ehouse_rows ?? 2}
                                            onChange={(e) =>
                                                setCampusConfig({
                                                    ...campusConfig,
                                                    ehouse_rows: parseInt(e.target.value, 10) || 0,
                                                })
                                            }
                                            className="sp-number-input"
                                        />
                                    </div>
                                    <div className="sp-flex-col">
                                        <span className="sp-input-label">Columns</span>
                                        <input
                                            type="number"
                                            min="0"
                                            value={campusConfig.ehouse_cols ?? 2}
                                            onChange={(e) =>
                                                setCampusConfig({
                                                    ...campusConfig,
                                                    ehouse_cols: parseInt(e.target.value, 10) || 0,
                                                })
                                            }
                                            className="sp-number-input"
                                        />
                                    </div>
                                </div>
                                <div className="sp-grid-2">
                                    <div className="sp-flex-col">
                                        <span className="sp-input-label">Length (m)</span>
                                        <input
                                            type="number"
                                            min="0.1"
                                            step="0.1"
                                            value={campusConfig.ehouse_length}
                                            onChange={(e) =>
                                                setCampusConfig({
                                                    ...campusConfig,
                                                    ehouse_length: parseFloat(e.target.value) || 0,
                                                })
                                            }
                                            className="sp-number-input"
                                        />
                                    </div>
                                    <div className="sp-flex-col">
                                        <span className="sp-input-label">Width (m)</span>
                                        <input
                                            type="number"
                                            min="0.1"
                                            step="0.1"
                                            value={campusConfig.ehouse_width}
                                            onChange={(e) =>
                                                setCampusConfig({
                                                    ...campusConfig,
                                                    ehouse_width: parseFloat(e.target.value) || 0,
                                                })
                                            }
                                            className="sp-number-input"
                                        />
                                    </div>
                                </div>
                                <div className="sp-grid-2">
                                    <div className="sp-flex-col">
                                        <span className="sp-input-label">Gap (m)</span>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.1"
                                            value={campusConfig.ehouse_gap}
                                            onChange={(e) =>
                                                setCampusConfig({
                                                    ...campusConfig,
                                                    ehouse_gap: parseFloat(e.target.value) || 0,
                                                })
                                            }
                                            className="sp-number-input"
                                        />
                                    </div>
                                    <div className="sp-flex-col">
                                        <span className="sp-input-label">Field gap (m)</span>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.1"
                                            value={campusConfig.field_gap}
                                            onChange={(e) =>
                                                setCampusConfig({
                                                    ...campusConfig,
                                                    field_gap: parseFloat(e.target.value) || 0,
                                                })
                                            }
                                            className="sp-number-input"
                                        />
                                    </div>
                                </div>
                                <div style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1' }}>Central datahall</div>
                                <label className="sp-flex-row sp-gap-2" style={{ alignItems: 'center', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={!!campusConfig.central_enabled}
                                        onChange={(e) =>
                                            setCampusConfig({
                                                ...campusConfig,
                                                central_enabled: e.target.checked,
                                            })
                                        }
                                        className="sp-checkbox"
                                    />
                                    <span className="sp-input-label">Include central hall</span>
                                </label>
                                {campusConfig.central_enabled && (
                                    <>
                                        <div className="sp-grid-2">
                                            <div className="sp-flex-col">
                                                <span className="sp-input-label">Length (m)</span>
                                                <input
                                                    type="number"
                                                    min="0.1"
                                                    step="0.1"
                                                    value={campusConfig.central_length}
                                                    onChange={(e) =>
                                                        setCampusConfig({
                                                            ...campusConfig,
                                                            central_length: parseFloat(e.target.value) || 0,
                                                        })
                                                    }
                                                    className="sp-number-input"
                                                />
                                            </div>
                                            <div className="sp-flex-col">
                                                <span className="sp-input-label">Width (m)</span>
                                                <input
                                                    type="number"
                                                    min="0.1"
                                                    step="0.1"
                                                    value={campusConfig.central_width}
                                                    onChange={(e) =>
                                                        setCampusConfig({
                                                            ...campusConfig,
                                                            central_width: parseFloat(e.target.value) || 0,
                                                        })
                                                    }
                                                    className="sp-number-input"
                                                />
                                            </div>
                                        </div>
                                        <div className="sp-flex-col">
                                            <span className="sp-input-label">Wings</span>
                                            <div className="sp-toggle-group">
                                                {WING_OPTIONS.map(({ id, label }) => {
                                                    const selected = (campusConfig.wings || []).includes(id);
                                                    return (
                                                        <button
                                                            key={id}
                                                            type="button"
                                                            onClick={() => {
                                                                const wings = selected
                                                                    ? campusConfig.wings.filter((wing) => wing !== id)
                                                                    : [...(campusConfig.wings || []), id];
                                                                setCampusConfig({ ...campusConfig, wings });
                                                            }}
                                                            className={`sp-toggle-btn ${selected ? 'active' : ''}`}
                                                        >
                                                            {label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    <ModulePalette
                        onDragStart={onPaletteDragStart}
                        activeType={paletteDragType}
                        tiles={infraPaletteTiles}
                    />
                    <p className="sp-manual-status">
                        Place M3 (GIS) and M4 (TX) on the site before running group optimization.
                    </p>

                    {mode === 'dxf' && dxfLayers && Object.keys(dxfLayers).length > 0 && (
                        <div className="sp-flex-col sp-gap-3">
                            <div
                                className="sp-flex-row sp-gap-2 sp-section-title"
                                style={{ borderBottom: '1px solid #1e293b', paddingBottom: '0.5rem', color: '#2dd4bf' }}
                            >
                                <Layers size={16} />
                                <span>DXF Layer Control</span>
                            </div>
                            <div className="sp-layer-list">
                                {Object.keys(dxfLayers).map((layer) => (
                                    <label key={layer} className="sp-layer-item">
                                        <input
                                            type="checkbox"
                                            checked={visibleLayers.includes(layer)}
                                            onChange={() => toggleLayer(layer)}
                                            className="sp-checkbox"
                                        />
                                        <span className="truncate">{layer}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {mode === 'dxf' && (
                        <div className="sp-flex-col sp-gap-3">
                            <div className="sp-input-group">
                                <label className="sp-section-title">Upload CAD File</label>
                                <input
                                    type="file"
                                    accept=".dxf"
                                    onChange={onUploadDxf}
                                    className="sp-file-input"
                                />
                            </div>
                            <div className="sp-grid-2">
                                <div className="sp-flex-col">
                                    <span className="sp-input-label">Drawing Units</span>
                                    <select
                                        value={dxfUnits}
                                        onChange={(e) => setDxfUnits(e.target.value)}
                                        className="sp-number-input"
                                    >
                                        {DXF_UNIT_OPTIONS.map((opt) => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="sp-flex-col">
                                    <span className="sp-input-label">Custom Scale</span>
                                    <input
                                        type="number"
                                        min="0.0001"
                                        step="any"
                                        value={dxfScale}
                                        onChange={(e) => setDxfScale(parseFloat(e.target.value) || 1)}
                                        className="sp-number-input"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {panelTab === 'manual' && (
                <div className="sp-flex-col sp-gap-3">
                    <label className="sp-flex-row sp-gap-2" style={{ alignItems: 'center', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={manualMode}
                            onChange={(e) => setManualMode(e.target.checked)}
                            className="sp-checkbox"
                        />
                        <span className="sp-section-title" style={{ color: '#a78bfa' }}>
                            Enable Manual Mode
                        </span>
                    </label>

                    {!manualMode && (
                        <p className="sp-manual-status">
                            Turn on manual mode to split the usable area and place module hatching by hand.
                            You can also toggle it from the drawing window.
                        </p>
                    )}

                    {manualMode && (
                        <>
                            <div className="sp-tool-grid">
                                {MANUAL_TOOLS.map(({ id, label, icon: Icon }) => (
                                    <button
                                        key={id}
                                        type="button"
                                        className={`sp-tool-mode-btn ${toolMode === id ? 'active' : ''}`}
                                        onClick={() => {
                                            setToolMode(id);
                                            if (id === 'hatchOrigin') {
                                                setHatchAngleStep(null);
                                            }
                                        }}
                                    >
                                        <Icon size={14} style={{ marginRight: 4, verticalAlign: -2 }} />
                                        {label}
                                    </button>
                                ))}
                            </div>

                            {toolMode === 'corridor' && (
                                <div className="sp-flex-col">
                                    <span className="sp-input-label">Corridor Width (m)</span>
                                    <input
                                        type="number"
                                        min="0.1"
                                        step="0.1"
                                        value={getDisplay('corridor-width', corridorWidth)}
                                        onChange={(e) =>
                                            handleNumericDraftChange(
                                                'corridor-width',
                                                e.target.value,
                                                (val) => setCorridorWidth(val),
                                            )
                                        }
                                        onBlur={() =>
                                            handleNumericDraftBlur(
                                                'corridor-width',
                                                corridorWidth,
                                                (val) => setCorridorWidth(val),
                                            )
                                        }
                                        className="sp-number-input"
                                    />
                                </div>
                            )}

                            <div className="sp-manual-status">
                                <div>
                                    Sub-areas: <strong>{subAreas.length}</strong>
                                </div>
                                <div>
                                    Selected: <strong>{selectedAreaIds.length}</strong>
                                    {selectedAreaIds.length > 0 && (
                                        <span>
                                            {' '}
                                            (
                                            {selectedAreaIds
                                                .map((id) => {
                                                    const idx = subAreas.findIndex((a) => a.id === id);
                                                    return idx >= 0 ? formatAreaLabel(idx) : id;
                                                })
                                                .join(', ')}
                                            )
                                        </span>
                                    )}
                                </div>
                                <div>
                                    Manual modules: <strong>{manualModuleCount}</strong>
                                </div>
                            </div>

                            {subAreas.length > 0 && (
                                <div className="sp-area-hatch-list">
                                    <div className="sp-section-title" style={{ marginBottom: '0.5rem' }}>
                                        Area Hatch Settings
                                    </div>
                                    {subAreas.map((area, idx) => {
                                        const label = formatAreaLabel(idx);
                                        const cfg = areaHatchConfigs[area.id] || {};
                                        const isSelected = selectedAreaIds.includes(area.id);
                                        return (
                                            <div
                                                key={area.id}
                                                className={`sp-area-hatch-card${isSelected ? ' selected' : ''}`}
                                            >
                                                <button
                                                    type="button"
                                                    className="sp-area-hatch-label"
                                                    onClick={() =>
                                                        setSelectedAreaIds(
                                                            isSelected ? [] : [area.id],
                                                        )
                                                    }
                                                >
                                                    {label}
                                                </button>
                                                <div className="sp-grid-2">
                                                    <div className="sp-flex-col">
                                                        <span className="sp-input-label">Origin X</span>
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            value={getDisplay(
                                                                `${area.id}-ox`,
                                                                cfg.origin?.[0],
                                                            )}
                                                            onChange={(e) =>
                                                                handleNumericDraftChange(
                                                                    `${area.id}-ox`,
                                                                    e.target.value,
                                                                    (val) => {
                                                                        const oy = cfg.origin?.[1] ?? 0;
                                                                        updateAreaHatchConfig(area.id, {
                                                                            origin: [val, oy],
                                                                        });
                                                                    },
                                                                )
                                                            }
                                                            onBlur={() =>
                                                                handleNumericDraftBlur(
                                                                    `${area.id}-ox`,
                                                                    cfg.origin?.[0],
                                                                    (val) => {
                                                                        const oy = cfg.origin?.[1] ?? 0;
                                                                        updateAreaHatchConfig(area.id, {
                                                                            origin: [val, oy],
                                                                        });
                                                                    },
                                                                )
                                                            }
                                                            className="sp-number-input"
                                                            placeholder="—"
                                                        />
                                                    </div>
                                                    <div className="sp-flex-col">
                                                        <span className="sp-input-label">Origin Y</span>
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            value={getDisplay(
                                                                `${area.id}-oy`,
                                                                cfg.origin?.[1],
                                                            )}
                                                            onChange={(e) =>
                                                                handleNumericDraftChange(
                                                                    `${area.id}-oy`,
                                                                    e.target.value,
                                                                    (val) => {
                                                                        const ox = cfg.origin?.[0] ?? 0;
                                                                        updateAreaHatchConfig(area.id, {
                                                                            origin: [ox, val],
                                                                        });
                                                                    },
                                                                )
                                                            }
                                                            onBlur={() =>
                                                                handleNumericDraftBlur(
                                                                    `${area.id}-oy`,
                                                                    cfg.origin?.[1],
                                                                    (val) => {
                                                                        const ox = cfg.origin?.[0] ?? 0;
                                                                        updateAreaHatchConfig(area.id, {
                                                                            origin: [ox, val],
                                                                        });
                                                                    },
                                                                )
                                                            }
                                                            className="sp-number-input"
                                                            placeholder="—"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="sp-flex-col">
                                                    <span className="sp-input-label">Angle (°)</span>
                                                    <input
                                                        type="text"
                                                        inputMode="decimal"
                                                        value={getDisplay(`${area.id}-angle`, cfg.angle)}
                                                        onChange={(e) =>
                                                            handleNumericDraftChange(
                                                                `${area.id}-angle`,
                                                                e.target.value,
                                                                (val) => {
                                                                    updateAreaHatchConfig(area.id, {
                                                                        angle: val,
                                                                    });
                                                                },
                                                            )
                                                        }
                                                        onBlur={() =>
                                                            handleNumericDraftBlur(
                                                                `${area.id}-angle`,
                                                                cfg.angle,
                                                                (val) => {
                                                                    updateAreaHatchConfig(area.id, {
                                                                        angle: val,
                                                                    });
                                                                },
                                                            )
                                                        }
                                                        className="sp-number-input"
                                                        placeholder="—"
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            <button type="button" onClick={onApplyHatch} className="sp-btn-primary">
                                <Grid3x3 size={16} />
                                Apply Hatch to Selection
                            </button>

                            <ModulePalette
                                onDragStart={onPaletteDragStart}
                                activeType={paletteDragType}
                            />

                            <p className="sp-manual-status" style={{ marginTop: '0.25rem' }}>
                                {toolMode === 'split' &&
                                    'Click snapped points to chain a division polyline. Finish on a border/corner or close on the first point for an interior cell. Enter finishes when the last point is on a border. Esc cancels; right-click removes the last point.'}
                                {toolMode === 'corridor' &&
                                    'Click snapped points along the corridor centerline. Enter or double-click finishes; Esc cancels.'}
                                {toolMode === 'select' &&
                                    'Select areas, cut lines, or modules. Drag modules to reposition. Shift+click areas for multi-select. Right-click module to rotate.'}
                                {toolMode === 'hatchOrigin' &&
                                    (selectedAreaIds.length === 0
                                        ? 'Select an area first, then click to set hatch origin. Angle setup follows automatically.'
                                        : hatchAngleStep === 'reference'
                                          ? 'Click the angle reference point on the plot.'
                                          : hatchAngleStep === 'direction'
                                            ? 'Adjust hatch angle on the plot (±2° edge snap). Tab keeps the current angle when redoing origin.'
                                            : 'Click hatch origin for the selected area.')}
                            </p>
                        </>
                    )}
                </div>
            )}

            <div className="sp-flex-col sp-gap-2 mt-auto" style={{ paddingTop: '0.5rem' }}>
                <button type="button" onClick={onRunOptimize} className="sp-btn-primary">
                    <CheckCircle size={16} />
                    Run Optimizer
                </button>
                {results?.groups?.length > 0 && (
                    <button type="button" onClick={onAcceptAllGroups} className="sp-btn-secondary">
                        Accept All Groups
                    </button>
                )}
                {m2Dirty && (
                    <div className="sp-m2-dirty-hint">
                        M2 positions changed — run optimizer to refresh M1 layout
                    </div>
                )}
                <button type="button" onClick={onClearAll} className="sp-btn-secondary">
                    <RotateCcw size={14} />
                    Clear Layout
                </button>
                {canExportDxf && (
                    <button
                        type="button"
                        onClick={onExportDxf}
                        className="sp-btn-secondary"
                        disabled={exportDisabled}
                    >
                        <Download size={14} />
                        Export DXF
                    </button>
                )}
            </div>

            {results && (
                <div className="sp-metrics">
                    <div style={{ fontWeight: '600', color: '#2dd4bf' }}>Layout Metrics</div>
                    <div className="sp-metrics-row">
                        <span>Datahalls (M1):</span>
                        <span className="sp-metrics-val">{results.module1_count}</span>
                    </div>
                    {manualModuleCount > 0 && (
                        <div className="sp-metrics-row">
                            <span>Manual Hatch Count:</span>
                            <span className="sp-metrics-val">{manualModuleCount}</span>
                        </div>
                    )}
                    <div className="sp-metrics-row">
                        <span>{results.mode === 'campus' ? 'eHouses:' : 'MV-LV trains (M2):'}</span>
                        <span className="sp-metrics-val">{results.module2_count}</span>
                    </div>
                    {results.mode === 'campus' && (
                        <div className="sp-metrics-row">
                            <span>Campus layout:</span>
                            <span className="sp-metrics-val">
                                {CAMPUS_LAYOUT_LABELS[results.strategy_name] || results.strategy_name}
                            </span>
                        </div>
                    )}
                    {results.group_count != null && (
                        <div className="sp-metrics-row">
                            <span>Recommended Groups:</span>
                            <span className="sp-metrics-val">{results.group_count}</span>
                        </div>
                    )}
                    {results.occupied_area_m2 != null && (results.group_count > 0 || results.mode === 'campus') && (
                        <div className="sp-metrics-row">
                            <span>Land taken:</span>
                            <span className="sp-metrics-val">{Math.round(results.occupied_area_m2)} m²</span>
                        </div>
                    )}
                    {results.max_fill_reference != null && (
                        <div className="sp-metrics-row">
                            <span>Max Fill Reference:</span>
                            <span className="sp-metrics-val">{results.max_fill_reference} M1</span>
                        </div>
                    )}
                    <div className="sp-metrics-row">
                        <span>Best Sweep Angle:</span>
                        <span className="sp-metrics-val">{results.best_angle}°</span>
                    </div>
                    <div className="sp-metrics-row">
                        <span>Property Area:</span>
                        <span className="sp-metrics-val">{Math.round(results.property_area)} m²</span>
                    </div>
                    <div className="sp-metrics-row">
                        <span>Usable Area:</span>
                        <span className="sp-metrics-val">{Math.round(results.usable_area)} m²</span>
                    </div>
                </div>
            )}
        </div>
    );
}
